import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Users, Euro, ChevronDown, ChevronUp, Award, Coffee, Sparkles, Zap, Building2, BadgeCheck } from "lucide-react";
import { useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import coursesBanner from "@/assets/courses-banner.png";

interface CourseData {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  duration: string;
  participants: string;
  price: string;
  priceNote?: string;
  forWhom: string[];
  description: string;
  objective: string;
  contents: string[];
  evaluation?: string;
  note?: string;
  extraInfo?: React.ReactNode;
}

const CourseCard = ({ course, t }: { course: CourseData; t: (s: string, k: string) => any }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card className="overflow-hidden border-2 hover:border-gold transition-all duration-300">
      <div 
        className="p-6 cursor-pointer h-[180px] flex flex-col justify-between"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="text-gold p-3 bg-gold/10 rounded-xl flex-shrink-0">
              {course.icon}
            </div>
            <div className="min-h-[48px]">
              <h3 className="text-xl font-bold text-foreground leading-tight">{course.title}</h3>
              <p className="text-sm text-gold font-medium">{course.subtitle}</p>
            </div>
          </div>
          <div className="text-muted-foreground flex-shrink-0">
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
        
        <div className="flex flex-wrap gap-4 mt-4 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>{course.duration}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="w-4 h-4" />
            <span>{course.participants}</span>
          </div>
          <div className="flex items-center gap-2 text-gold font-semibold">
            <Euro className="w-4 h-4" />
            <span>{course.price}</span>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-6 pb-6 border-t border-border pt-6 space-y-6 animate-in slide-in-from-top-2 duration-300">
          <div>
            <h4 className="font-semibold text-foreground mb-2">{t("courses", "forWhom")}</h4>
            <ul className="space-y-1">
              {course.forWhom.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-foreground/80 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0"></div>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">{t("courses", "description")}</h4>
            <p className="text-foreground/80 text-sm">{course.description}</p>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">{t("courses", "objective")}</h4>
            <p className="text-foreground/80 text-sm">{course.objective}</p>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">{t("courses", "contents")}</h4>
            <ul className="grid md:grid-cols-2 gap-2">
              {course.contents.map((content, idx) => (
                <li key={idx} className="flex items-start gap-2 text-foreground/80 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0"></div>
                  <span>{content}</span>
                </li>
              ))}
            </ul>
          </div>

          {course.evaluation && (
            <div className="bg-muted/50 p-4 rounded-lg">
              <h4 className="font-semibold text-foreground mb-2">{t("courses", "evaluation")}</h4>
              <p className="text-foreground/80 text-sm">{course.evaluation}</p>
            </div>
          )}

          {course.note && (
            <div className="bg-gold/10 p-4 rounded-lg">
              <p className="text-foreground/80 text-sm"><strong>{t("courses", "nota")}</strong> {course.note}</p>
            </div>
          )}

          {course.extraInfo}

          <Button variant="premium" className="w-full" asChild>
            <a href="#contacto">{t("courses", "reservarFormacion")}</a>
          </Button>
        </div>
      )}
    </Card>
  );
};

export const Courses = () => {
  const { t } = useLanguage();

  const courses: CourseData[] = [
    {
      id: "barista-completa",
      title: t("courses", "c1Title"),
      subtitle: t("courses", "c1Subtitle"),
      icon: <Coffee className="w-6 h-6" />,
      duration: t("courses", "c1Duration"),
      participants: t("courses", "c1Participants"),
      price: t("courses", "c1Price"),
      forWhom: t("courses", "c1ForWhom"),
      description: t("courses", "c1Desc"),
      objective: t("courses", "c1Objective"),
      contents: t("courses", "c1Contents"),
      evaluation: t("courses", "c1Evaluation"),
    },
    {
      id: "focus",
      title: t("courses", "c2Title"),
      subtitle: t("courses", "c2Subtitle"),
      icon: <Zap className="w-6 h-6" />,
      duration: t("courses", "c2Duration"),
      participants: t("courses", "c2Participants"),
      price: t("courses", "c2Price"),
      forWhom: t("courses", "c2ForWhom"),
      description: t("courses", "c2Desc"),
      objective: t("courses", "c2Objective"),
      contents: t("courses", "c2Contents"),
    },
    {
      id: "latte-art-base",
      title: t("courses", "c3Title"),
      subtitle: t("courses", "c3Subtitle"),
      icon: <Sparkles className="w-6 h-6" />,
      duration: t("courses", "c3Duration"),
      participants: t("courses", "c3Participants"),
      price: t("courses", "c3Price"),
      forWhom: t("courses", "c3ForWhom"),
      description: t("courses", "c3Desc"),
      objective: t("courses", "c3Objective"),
      contents: t("courses", "c3Contents"),
    },
    {
      id: "latte-art-avanzado",
      title: t("courses", "c4Title"),
      subtitle: t("courses", "c4Subtitle"),
      icon: <Award className="w-6 h-6" />,
      duration: t("courses", "c4Duration"),
      participants: t("courses", "c4Participants"),
      price: t("courses", "c4Price"),
      forWhom: t("courses", "c4ForWhom"),
      description: t("courses", "c4Desc"),
      objective: t("courses", "c4Objective"),
      contents: t("courses", "c4Contents"),
    },
    {
      id: "workflow",
      title: t("courses", "c5Title"),
      subtitle: t("courses", "c5Subtitle"),
      icon: <Zap className="w-6 h-6" />,
      duration: t("courses", "c5Duration"),
      participants: t("courses", "c5Participants"),
      price: t("courses", "c5Price"),
      forWhom: t("courses", "c5ForWhom"),
      description: t("courses", "c5Desc"),
      objective: t("courses", "c5Objective"),
      contents: t("courses", "c5Contents"),
    },
    {
      id: "equipos-empresas",
      title: t("courses", "c6Title"),
      subtitle: t("courses", "c6Subtitle"),
      icon: <Building2 className="w-6 h-6" />,
      duration: t("courses", "c6Duration"),
      participants: t("courses", "c6Participants"),
      price: t("courses", "c6Price"),
      forWhom: t("courses", "c6ForWhom"),
      description: t("courses", "c6Desc"),
      objective: t("courses", "c6Objective"),
      contents: t("courses", "c6Contents"),
      note: t("courses", "c6Note"),
    }
  ];


  return (
    <section id="cursos" className="py-24 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            {t("courses", "sectionTitle")}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {t("courses", "sectionSubtitle")}
          </p>
          <div className="w-24 h-1 bg-gold mx-auto mt-6"></div>
        </div>

        <div className="relative h-72 md:h-[28rem] rounded-2xl overflow-hidden mb-16" style={{
          backgroundImage: `url(${coursesBanner})`,
          backgroundSize: "cover",
          backgroundPosition: "center 40%"
        }}>
          <div className="absolute inset-0 bg-gradient-to-t from-espresso/90 to-transparent flex items-end">
            <div className="p-8 text-cream">
              <h3 className="text-3xl font-bold mb-2"></h3>
              <p className="text-lg"></p>
            </div>
          </div>
        </div>

        {/* Formaciones principales */}
        <div className="grid lg:grid-cols-2 gap-6 mb-12 items-start">
          {courses.map((course) => (
            <CourseCard key={course.id} course={course} t={t} />
          ))}
        </div>

        {/* Certificaciones LAGS */}
        <div className="bg-card rounded-2xl p-8 border border-border mb-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="text-gold p-3 bg-gold/10 rounded-xl">
              <BadgeCheck className="w-8 h-8" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-foreground">{t("courses", "certTitle")}</h3>
              <p className="text-muted-foreground mt-2">{t("courses", "certDesc")}</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold text-foreground mb-3">{t("courses", "certSessionTitle")}</h4>
              <ul className="space-y-2 text-foreground/80">
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gold" />
                  <span>{t("courses", "certDuration")}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Euro className="w-4 h-4 text-gold" />
                  <span>{t("courses", "certPrice")}</span>
                </li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3">{t("courses", "certExamCost")}</p>
            </div>
            
            <div>
              <h4 className="font-semibold text-foreground mb-3">{t("courses", "certExamPrices")}</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between bg-muted/50 p-2 rounded">
                  <span>Blanco</span>
                  <span className="font-semibold text-gold">120€</span>
                </div>
                <div className="flex justify-between bg-muted/50 p-2 rounded">
                  <span>Naranja</span>
                  <span className="font-semibold text-gold">120€</span>
                </div>
                <div className="flex justify-between bg-muted/50 p-2 rounded">
                  <span>Verde</span>
                  <span className="font-semibold text-gold">160€</span>
                </div>
                <div className="flex justify-between bg-muted/50 p-2 rounded">
                  <span>Rojo</span>
                  <span className="font-semibold text-gold">160€</span>
                </div>
                <div className="flex justify-between bg-muted/50 p-2 rounded">
                  <span>Negro</span>
                  <span className="font-semibold text-gold">200€</span>
                </div>
                <div className="flex justify-between bg-muted/50 p-2 rounded">
                  <span>Oro</span>
                  <span className="font-semibold text-gold">480€</span>
                </div>
                <div className="flex justify-between bg-gold/20 p-2 rounded col-span-2">
                  <span>Examiner Calibration</span>
                  <span className="font-semibold text-gold">250€</span>
                </div>
              </div>
            </div>
          </div>
          
          <Button variant="premium" className="w-full mt-6" asChild>
            <a href="#contacto">{t("courses", "solicitarCert")}</a>
          </Button>
        </div>

      </div>
    </section>
  );
};
